import React from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, Toolbar, Pagination, ResponsiveTable, StatusBadge, TableSkeleton, EmptyState } from "@/admin/ui";
import { Search, Download, ShieldAlert, Snowflake, User } from "lucide-react";

const fmt$ = (cents) => `$${((Number(cents || 0)) / 100).toFixed(2)}`;

function RiskBadge({ score }) {
  if (!score) return <span className="text-white/30 text-xs">—</span>;
  const tone = score >= 4 ? "danger" : score >= 2 ? "warning" : "neutral";
  return <StatusBadge tone={tone} testid="risk-badge">risk {score}</StatusBadge>;
}

export default function AdminUsers() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = React.useState(params.get("q") || "");
  const [filter, setFilter] = React.useState(params.get("filter") || "");
  const [page, setPage] = React.useState(parseInt(params.get("page") || "1", 10));
  const [data, setData] = React.useState(null);
  const perPage = 25;

  // Debounce search into URL & fetch
  React.useEffect(() => {
    const h = setTimeout(() => {
      const next = {};
      if (q) next.q = q;
      if (filter) next.filter = filter;
      if (page !== 1) next.page = String(page);
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(h);
  }, [q, filter, page, setParams]);

  const load = React.useCallback(() => {
    const p = { page, per_page: perPage };
    if (q) p.q = q;
    if (filter) p.filter = filter;
    api.get("/api/admin/users", { params: p }).then((r) => setData(r.data));
  }, [q, filter, page]);
  React.useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (filter) p.set("filter", filter);
    window.location.href = `${API_BASE}/admin/users.csv?${p.toString()}`;
  };
  const setF = (v) => { setFilter(v); setPage(1); };
  const clearAll = () => { setQ(""); setFilter(""); setPage(1); };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / perPage)) : 1;
  const activeFilters = (q ? 1 : 0) + (filter ? 1 : 0);

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <PageHeader
        title={t("admin.users.title")}
        description={t("admin.users.subtitle")}
        primary={<button className="lp-btn-secondary text-xs py-1.5 px-3" onClick={exportCsv} data-testid="users-export-csv">
          <Download className="w-3.5 h-3.5" /> {t("admin.users.csv")}
        </button>}
      />

      <Toolbar right={activeFilters > 0 ? <button className="lp-btn-ghost" onClick={clearAll} data-testid="users-clear-filters">Clear ({activeFilters})</button> : null}>
        <div className="flex-1 relative min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder={t("admin.users.search_ph")}
            className="lp-input w-full pl-9 py-2 text-sm" data-testid="users-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {["", "paying", "frozen", "suspended", "admin"].map((f) => (
            <button key={f || "all"} onClick={() => setF(f)}
              className={`text-xs px-2.5 py-1 rounded ${filter === f ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/5"}`}
              data-testid={`users-filter-${f || "all"}`}>{t(`admin.users.filter.${f || "all"}`)}</button>
          ))}
        </div>
      </Toolbar>

      {!data ? <TableSkeleton rows={8} cols={7} /> : data.items.length === 0 ? (
        <EmptyState title={t("admin.users.empty")} action={activeFilters > 0 && <button className="lp-btn-secondary text-xs" onClick={clearAll}>Clear filters</button>} />
      ) : (
        <ResponsiveTable minWidth={960}>
          <thead className="lp-table-sticky"><tr>
            <th>{t("admin.users.col.user")}</th>
            <th className="text-right">{t("admin.users.col.credits")}</th>
            <th className="text-right">{t("admin.users.col.lifetime")}</th>
            <th className="text-right">{t("admin.users.col.orders")}</th>
            <th>{t("admin.users.col.status")}</th>
            <th className="text-right">{t("admin.users.col.risk")}</th>
            <th>{t("admin.users.col.joined")}</th>
            <th className="text-right"></th>
          </tr></thead>
          <tbody>
            {data.items.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>
                  <div className="text-white/95 text-sm truncate max-w-[220px]" title={u.name}>{u.name || "—"}</div>
                  <div className="text-white/50 text-xs truncate max-w-[220px]" title={u.email}>{u.email}</div>
                </td>
                <td className="text-right text-white/85 tabular-nums">{(u.credits || 0).toLocaleString()}</td>
                <td className="text-right text-white/85 tabular-nums">
                  {fmt$(u.lifetime_gross_cents)}
                  {u.lifetime_refunded_cents > 0 && <div className="text-red-300/70 text-[10px]">−{fmt$(u.lifetime_refunded_cents)}</div>}
                </td>
                <td className="text-right text-white/70 tabular-nums">{u.lifetime_orders}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {u.active === false && <StatusBadge tone="danger">{t("admin.users.suspended")}</StatusBadge>}
                    {u.topup_frozen && <StatusBadge tone="warning" icon={Snowflake}>{t("admin.users.frozen")}</StatusBadge>}
                    {u.open_disputes > 0 && <StatusBadge tone="danger" icon={ShieldAlert}>{u.open_disputes}</StatusBadge>}
                    {u.role === "admin" && <StatusBadge tone="brand">Admin</StatusBadge>}
                    {u.active !== false && !u.topup_frozen && !u.open_disputes && <StatusBadge tone="success">Active</StatusBadge>}
                  </div>
                </td>
                <td className="text-right"><RiskBadge score={u.risk_score} /></td>
                <td className="text-white/60 text-xs whitespace-nowrap tabular-nums">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="text-right">
                  <NavLink to={`/admin/customers/${u.id}`} className="lp-btn-ghost" data-testid={`customer-360-${u.id}`}>
                    <User className="w-3 h-3" /> 360
                  </NavLink>
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}

      {data && <Pagination page={page} totalPages={totalPages} onChange={setPage} total={data.total} unitLabel={t("admin.users.total")} />}
    </div>
  );
}
